import { Box, Icon, IconButton, Text, useBreakpointValue } from "@chakra-ui/react";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react/dist/ssr";
import { useEffect, useRef, useState } from "react";

import useQuery from "@/hooks/useQuery";

const FeaturedCarousel = ({
    children,
    gap = 50,
    valuesLength,
    isSearchCard,
    cardsToShow = {
        base: 1,
        lg: isSearchCard && valuesLength === 1 ? 1 : 2,
        xl: isSearchCard && valuesLength <= 3 ? valuesLength : 3,
    },
}) => {
    const containerRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const totalItems = children.length;
    const currentCards = useBreakpointValue(cardsToShow) || 1;
    const { queryParams } = useQuery();

    // Dimensiones fijas de las cards
    const cardWidth = useBreakpointValue({ base: 320, sm: 364 }) || 364;
    const cardHeight =
        useBreakpointValue({ base: isSearchCard ? 560 : 596, md: isSearchCard ? 560 : 596 }) || 596;

    // Ancho total del carrusel
    const totalWidth = totalItems * (cardWidth + gap);
    const visibleWidth = currentCards * (cardWidth + gap) - gap; // Ancho exacto visible

    // Asegurar que el índice no permita fragmentos
    useEffect(() => {
        setCurrentIndex((prev) => Math.min(prev, Math.max(0, totalItems - currentCards)));
    }, [currentCards, totalItems]);

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex((prev) => Math.max(0, prev - 1));
        }
    };

    const handleNext = () => {
        if (currentIndex < totalItems - currentCards) {
            setCurrentIndex((prev) => Math.min(totalItems - currentCards, prev + 1));
        }
    };

    return (
        <Box position="relative" w={`${visibleWidth}px`} margin="0 auto">
            <Text
                fontWeight={600}
                mb={5}
                color="#1A202C"
                display={isSearchCard ? "flex" : "none"}
                justifyContent={{
                    base: "center",
                    // md: "start"
                }}
                fontSize={{ base: "md", md: "lg", xl: "xl" }}
                my={{ base: 4, md: 8 }}
                textAlign="center"
            >
                {`${valuesLength} ${valuesLength > 1 ? "Desarrollos encontrados" : "Desarrollo encontrado"} ${queryParams?.keywords ? `en ${queryParams?.keywords}` : ""}`}
            </Text>
            <Box ref={containerRef} overflow="hidden" width="100%">
                <Box
                    as="div"
                    animate={{ x: `-${currentIndex * (cardWidth + gap)}px` }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    transitionDuration="0.3s"
                    transitionTimingFunction="ease-out"
                    transform={`translateX(-${currentIndex * (cardWidth + gap)}px)`}
                    style={{
                        display: "flex",
                        gap: `${gap}px`,
                        width: `${totalWidth}px`,
                    }}
                >
                    {children?.length > 0 &&
                        children?.map((child, index) => (
                            <Box
                                key={index}
                                width={`${cardWidth}px`}
                                height={`${cardHeight}px`}
                                flexShrink={0}
                            >
                                {child}
                            </Box>
                        ))}
                </Box>
            </Box>
            <IconButton
                onClick={handlePrev}
                aria-label="Anterior"
                size="lg"
                borderRadius="full"
                position="absolute"
                left={{ base: `-${gap - 30}px`, sm: `-${gap + 15}px` }}
                top="50%"
                transform="translateY(-50%)"
                zIndex="10"
                bg="white"
                boxShadow="md"
                _hover={{ bg: "gray.100" }}
                disabled={currentIndex === 0}
                opacity={currentIndex === 0 ? 0.4 : 1}
                cursor={currentIndex === 0 ? "not-allowed" : "pointer"}
            >
                <Icon boxSize={6} color="black">
                    <CaretLeftIcon />
                </Icon>
            </IconButton>

            <IconButton
                onClick={handleNext}
                aria-label="Siguiente"
                size="lg"
                borderRadius="full"
                position="absolute"
                right={{ base: `-${gap - 30}px`, sm: `-${gap + 15}px` }}
                top="50%"
                transform="translateY(-50%)"
                zIndex="10"
                bg="white"
                boxShadow="md"
                _hover={{ bg: "gray.100" }}
                disabled={currentIndex >= totalItems - currentCards}
                opacity={currentIndex >= totalItems - currentCards ? 0.4 : 1}
                cursor={currentIndex >= totalItems - currentCards ? "not-allowed" : "pointer"}
            >
                <Icon boxSize={6} color="black">
                    <CaretRightIcon />
                </Icon>
            </IconButton>
        </Box>
    );
};

export default FeaturedCarousel;
